import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { useInView } from '../hooks/useInView';
import { useCountUp } from '../hooks/useCountUp';
import { useIsMobile } from '../hooks/useIsMobile';
import { SPEAKING } from '../data/speaking';

function StatBar() {
  const [ref, visible] = useInView();
  const isMobile = useIsMobile();
  const trained = useCountUp(3000, 2000, visible);
  const years = useCountUp(3, 1000, visible);
  const topics = useCountUp(6, 1000, visible);
  const stats = [
    { val: `${trained.toLocaleString()}+`, label: 'Professionals Trained' },
    { val: `${years}+`, label: 'Years Training' },
    { val: `${topics}`, label: 'Core Topics' },
    { val: 'M.A.', label: 'Forensic Psych' },
  ];
  return (
    <div ref={ref} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 1, marginTop: isMobile ? 48 : 72, background: 'rgba(255,255,255,0.04)', borderRadius: 12, overflow: 'hidden' }}>
      {stats.map((s, i) => (
        <div key={i} style={{ padding: isMobile ? '20px 12px' : '28px 20px', background: 'rgba(13,8,20,0.5)', textAlign: 'center' }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 24 : 30, fontWeight: 700, color: BRAND.warm, marginBottom: 4 }}>{s.val}</div>
          <div style={{ fontFamily: FONTS.body, fontSize: isMobile ? 10 : 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.05em' }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

interface HeroProps { onNav: (id: string) => void }

export function Hero({ onNav }: HeroProps) {
  const isMobile = useIsMobile();
  return (
    <section id="hero" style={{ minHeight: '100vh', background: `linear-gradient(160deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 40%, ${BRAND.slate} 100%)`, display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 15% 85%, rgba(124,58,165,0.18) 0%, transparent 50%), radial-gradient(circle at 85% 15%, rgba(51,184,184,0.12) 0%, transparent 45%)` }} />
      {!isMobile && <div style={{ position: 'absolute', left: '7%', top: '12%', bottom: '12%', width: 1, background: `linear-gradient(to bottom, transparent, ${BRAND.warm}35, transparent)` }} />}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '100px 20px 60px' : '130px 24px 80px', position: 'relative', zIndex: 1, width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: isMobile ? 36 : 56, alignItems: 'center' }}>
        <div>
          <Reveal><SectionLabel>Speaker · Consultant · Coercive Control Expert</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h1 style={{ fontFamily: FONTS.heading, fontWeight: 700, lineHeight: 1.08, marginBottom: isMobile ? 24 : 32 }}>
              <span style={{ display: 'block', fontSize: isMobile ? 36 : 'clamp(38px, 5.5vw, 66px)', color: BRAND.white, marginBottom: 8 }}>From Awareness</span>
              <span style={{ display: 'block', fontSize: isMobile ? 36 : 'clamp(38px, 5.5vw, 66px)', color: BRAND.warm }}>to Accountability.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.22}>
            <p style={{ fontFamily: FONTS.body, fontSize: isMobile ? 16 : 19, lineHeight: 1.75, color: 'rgba(255,255,255,0.72)', maxWidth: 580, marginBottom: 10 }}>
              When we call it an "incident," we miss the pattern.{isMobile ? ' ' : <br />}When we miss the pattern, we miss the risk.
            </p>
            <p style={{ fontFamily: FONTS.body, fontSize: isMobile ? 14 : 15, lineHeight: 1.75, color: 'rgba(255,255,255,0.45)', maxWidth: 540, marginBottom: isMobile ? 28 : 40 }}>
              {SPEAKING.positioning}
            </p>
          </Reveal>
          <Reveal delay={0.36}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 14 }}>
              <button onClick={() => onNav('lead')} style={{
                background: BRAND.warm, color: BRAND.navy, border: 'none',
                padding: isMobile ? '18px 24px' : '16px 36px', borderRadius: isMobile ? 12 : 8,
                fontSize: isMobile ? 16 : 15, fontWeight: 700, fontFamily: FONTS.body,
                cursor: 'pointer', transition: 'all 0.25s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: isMobile ? '100%' : 'auto',
                boxShadow: isMobile ? '0 4px 16px rgba(124,58,165,0.3)' : 'none',
              }}
                onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(124,58,165,0.35)'; } }}
                onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; } }}
              >📄 Download Free Resource Guide</button>

              <button onClick={() => onNav('assessments')} style={{
                background: 'transparent',
                color: isMobile ? BRAND.warm : BRAND.white,
                border: isMobile ? `2px solid ${BRAND.warm}` : '1px solid rgba(255,255,255,0.22)',
                padding: isMobile ? '16px 24px' : '16px 36px', borderRadius: isMobile ? 12 : 8,
                fontSize: 15, fontWeight: isMobile ? 600 : 500,
                fontFamily: FONTS.body, cursor: 'pointer', transition: 'all 0.25s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: isMobile ? '100%' : 'auto',
              }}
                onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.borderColor = BRAND.warm; e.currentTarget.style.color = BRAND.warm; } }}
                onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = BRAND.white; } }}
              >📊 Take a Free Assessment</button>
            </div>
            <a onClick={() => onNav('speaking')} role="link" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNav('speaking'); } }}
              style={{ display: 'inline-block', marginTop: isMobile ? 20 : 24, fontFamily: FONTS.body, fontSize: 14, fontWeight: 600, color: BRAND.teal, cursor: 'pointer', textDecoration: 'none', borderBottom: `1px solid ${BRAND.teal}55`, paddingBottom: 2 }}>
              Book Tracy to speak →
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.4}>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.45)', border: `1px solid ${BRAND.warm}33` }}>
            <iframe
              src="https://www.youtube.com/embed/Hu9r8h3CczI?si=ymEmHxs8ZhVpM0di"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          </div>
        </Reveal>
        </div>
        <Reveal delay={0.5}><StatBar /></Reveal>
      </div>
    </section>
  );
}
